import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { insightsRequest, insightsError } from 'Src/helpers/service/sltInsights';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { plainPointsToTimeString, plainPointsToDistanceString } from 'Src/helpers/score/score2';

export function fullSportResult(result: any): string {
  if (!result) return 'Not recorded';
  const value = result.value,
    status = result.status;
  if (value == null || (value === 0 && ['DNS', 'DNF', 'DQ', 'DSQ', 'DNQ', 'NM'].includes(status))) {
    return status || 'Not recorded';
  }
  let text = String(value);
  if (result.display === 'TIME') {
    text = result.mask ? plainPointsToTimeString(value, result.mask) || `${value} s` : `${value} s`;
  } else if (result.display === 'DISTANCE') {
    text = result.mask ? plainPointsToDistanceString(value, result.mask) || `${value / 100} m` : `${value / 100} m`;
  } else if (result.unit) text += ` ${result.unit}`;
  return text + (status ? ` · ${status}` : '');
}
const count = value => (value == null ? 'Protected / unavailable' : String(value));
const day = (value, timezone = 'UTC') =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: timezone });
function ResultLines({ item }: { item: any }) {
  if (!item.available) return <span className="sli-muted">{item.reason || 'No published result'}</span>;
  if (item.individual) {
    return (
      <span>
        {item.bestPlace
          ? `Best place: ${item.bestPlace}`
          : item.suppressed
          ? 'Individual results protected'
          : 'Published individual results'}
      </span>
    );
  }
  const best = item.best;
  return (
    <>
      <strong>
        {best?.name} · {best?.label}
      </strong>
      {best?.performance && <small>{fullSportResult(best.performance)}</small>}
      {item.teams?.length > 1 && (
        <details>
          <summary>All {item.teams.length} school teams</summary>
          {item.teams.map(team => (
            <p key={team.id}>
              {team.name} · {team.label}
              {team.performance && ` · ${fullSportResult(team.performance)}`}
            </p>
          ))}
        </details>
      )}
    </>
  );
}
export function SltSportResults({
  user,
  query,
  canStudents,
  onStudent
}: {
  user: AppUser;
  query: any;
  canStudents: boolean;
  onStudent?: (id: string) => void;
}) {
  const key = `${user.activeSchoolId}:${user.roleSessionKey}:${JSON.stringify(query)}`;
  const [state, setState] = React.useState<any>(null),
    [detail, setDetail] = React.useState<any>(null),
    [retry, setRetry] = React.useState(0);
  const current = React.useRef(key);
  current.current = key;
  React.useEffect(() => {
    let active = true;
    setState(null);
    setDetail(null);
    insightsRequest(user, 'results', query)
      .then(data => {
        if (active) setState({ key, data });
      })
      .catch(error => {
        if (active) setState({ key, error: insightsError(error) });
      });
    return () => {
      active = false;
    };
  }, [key, retry]);
  React.useEffect(() => {
    if (!detail) return;
    const previous = document.activeElement as HTMLElement,
      dialog = document.querySelector('.sli-sportResultsDrawer') as HTMLElement;
    if (!dialog) return;
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'School individual results');
    const items = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled)'
        )
      ).filter(item => !!item.offsetParent);
    items()[0]?.focus();
    const keydown = (event: KeyboardEvent) => {
      const list = items();
      if (event.key === 'Escape') {
        event.preventDefault();
        setDetail(null);
      }
      if (event.key === 'Tab' && list.length) {
        if (event.shiftKey && document.activeElement === list[0]) {
          event.preventDefault();
          list[list.length - 1].focus();
        } else if (!event.shiftKey && document.activeElement === list[list.length - 1]) {
          event.preventDefault();
          list[0].focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, [!!detail]);
  const open = async (id: string) => {
    const requested = key;
    setDetail({ loading: true });
    try {
      const value = await insightsRequest(user, `results/${id}`, query);
      if (current.current === requested) setDetail({ value });
    } catch (error) {
      if (current.current === requested) setDetail({ error: insightsError(error) });
    }
  };
  const data = state?.key === key ? state.data : null;
  return (
    <div className="sli-sportResults">
      <section className="sli-panel" id="slt-sport-results">
        <div className="sli-panelHead">
          <h2>Fixtures & individual results</h2>
        </div>
        <p className="sli-help">
          Latest published scores for events in the selected period. Team results cover the whole school; individual
          results use the selected roster. Scores do not change attendance-based participation.
        </p>
        {!state && <p aria-live="polite">Loading results…</p>}
        {state?.error && (
          <p role="alert" className="sli-error">
            {state.error}
            <button className="sli-button" type="button" onClick={() => setRetry(value => value + 1)}>
              Retry results
            </button>
          </p>
        )}
        {data && (
          <>
            <div className="sli-resultTotals">
              <span>
                <strong>{data.wdl.won}</strong> won
              </span>
              <span>
                <strong>{data.wdl.drawn}</strong> drawn
              </span>
              <span>
                <strong>{data.wdl.lost}</strong> lost
              </span>
              {data.wdl.tied > 0 && (
                <span>
                  <strong>{data.wdl.tied}</strong> tied
                </span>
              )}
              {data.wdl.noResult > 0 && (
                <span>
                  <strong>{data.wdl.noResult}</strong> no result
                </span>
              )}
            </div>
            <p className="sli-help">
              W/D/L counts head-to-head events with one school competitor. Internal matches, placings and individual
              events have their own result format.
            </p>
            <div className="sli-tableWrap">
              <table className="sli-table sli-resultsTable">
                <thead>
                  <tr>
                    <th>Date / event</th>
                    <th>Sport</th>
                    <th>School result</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.fixtures.map(item => (
                    <tr key={item.eventId}>
                      <th>
                        {item.name}
                        <small>{day(item.at, data.timezone)}</small>
                      </th>
                      <td>{item.sport}</td>
                      <td>
                        <ResultLines item={item} />
                      </td>
                      <td>
                        {item.individual && (
                          <button
                            type="button"
                            className="sli-link"
                            disabled={!canStudents || !item.available}
                            title={
                              !canStudents ? 'Individual result access requires student drill-down permission' : ''
                            }
                            onClick={() => open(item.eventId)}
                          >
                            Our top results →
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!data.fixtures.length && <p className="sli-empty">No sport events in this period.</p>}
          </>
        )}
      </section>
      {data && (
        <section className="sli-panel">
          <div className="sli-panelHead">
            <h2>Tournament achievements</h2>
          </div>
          <p className="sli-help">
            Progress through events up to {day(data.asOf, data.timezone)}. Current published scores are used; this does
            not reconstruct earlier versions of a result.
          </p>
          {data.tournaments.map(tournament => (
            <article className="sli-tournamentResult" key={tournament.id}>
              <h3>{tournament.name}</h3>
              {tournament.individual ? (
                <>
                  <div className="sli-resultTotals">
                    <span>
                      <strong>{count(tournament.studentsWithResults)}</strong> students with results
                    </span>
                    <span>
                      <strong>{count(tournament.performances)}</strong> performances
                    </span>
                    <span>
                      <strong>{count(tournament.uniqueMedallists)}</strong> medallists
                    </span>
                    <span>
                      <strong>{count(tournament.finalists)}</strong> finalists
                    </span>
                  </div>
                  <p>
                    Final-event medals: Gold {count(tournament.medals[0])} · Silver {count(tournament.medals[1])} ·
                    Bronze {count(tournament.medals[2])}
                  </p>
                  <p className="sli-help">
                    Medals and finalists use explicitly marked final events. {tournament.unclassifiedFinalEvents || 0}{' '}
                    published events have no final-stage classification.
                  </p>
                  <p>Recorded record performances: {count(tournament.recordPerformances)}</p>
                  <h4>Official school entries</h4>
                  {tournament.officialScores.map((entry, index) => (
                    <p key={index}>
                      <strong>{entry.team}</strong>
                      {entry.group ? ` · ${entry.group}` : ''} · {entry.points} points
                    </p>
                  ))}
                  <p className="sli-help">{tournament.pointsReason}</p>
                  <button
                    type="button"
                    className="sli-button"
                    disabled={!canStudents}
                    onClick={() => open(tournament.id)}
                  >
                    Top three school performances →
                  </button>
                </>
              ) : (
                <div>
                  {tournament.teams.map(team => (
                    <div className="sli-teamAchievement" key={team.teamId}>
                      <strong>{team.team}</strong>
                      {team.label ? (
                        <span>{team.label}</span>
                      ) : (
                        team.groups.map((group, index) => (
                          <span key={index}>
                            {group.name} ·{' '}
                            {group.place == null ? 'No ranked result' : `Place ${group.place} of ${group.total}`} ·{' '}
                            {group.points == null ? 'Points unavailable' : `${group.points} points`}
                            {group.provisional ? ' · Provisional' : ''}
                          </span>
                        ))
                      )}
                      {team.provisional && team.label && <small>Provisional / awaiting result</small>}
                    </div>
                  ))}
                  {!tournament.teams.length && <p className="sli-help">No retained school team could be identified.</p>}
                </div>
              )}
            </article>
          ))}
          {!data.tournaments.length && (
            <p className="sli-empty">No tournaments are linked to this period’s school events.</p>
          )}
        </section>
      )}
      <SimpleModal
        isOpen={!!detail}
        side="right"
        customClass="sli-drawer sli-sportResultsDrawer"
        title={detail?.value?.name || 'School individual results'}
        showFooter={false}
        onCloseClick={() => setDetail(null)}
      >
        <div className="sli-drawerBody">
          {detail?.loading && <p>Loading authorised results…</p>}
          {detail?.error && (
            <p role="alert" className="sli-error">
              {detail.error}
            </p>
          )}
          {detail?.value && (
            <>
              <p className="sli-help">
                Our best positions first. Full results are shown with their scoring format. This access is audited.
              </p>
              {detail.value.items.slice(0, 3).map((entry, index) => (
                <div className="sli-fact" key={entry.id + ':' + index}>
                  <strong>
                    {entry.firstName} {entry.lastName}
                  </strong>
                  <span>
                    {entry.year}
                    {entry.form ? ` · ${entry.form}` : ''}
                    {entry.event ? ` · ${entry.event}` : ''}
                  </span>
                  <strong>
                    {entry.place ? `Place ${entry.place} of ${entry.total}` : 'Not ranked'} ·{' '}
                    {fullSportResult(entry.result)}
                  </strong>
                  {entry.isRecord && <span className="sli-badge">Recorded record</span>}
                  {onStudent && (
                    <button
                      type="button"
                      className="sli-link"
                      onClick={() => {
                        setDetail(null);
                        onStudent(entry.id);
                      }}
                    >
                      View student →
                    </button>
                  )}
                </div>
              ))}
              {detail.value.items.length > 3 && (
                <details>
                  <summary>All school results in this event</summary>
                  {detail.value.items.slice(3).map((entry, index) => (
                    <div className="sli-fact" key={entry.id + ':' + index}>
                      <strong>
                        {entry.firstName} {entry.lastName} · {entry.year}
                      </strong>
                      <span>
                        {entry.place ? `Place ${entry.place} of ${entry.total}` : 'Not ranked'} ·{' '}
                        {fullSportResult(entry.result)}
                      </span>
                    </div>
                  ))}
                </details>
              )}
              {!detail.value.items.length && (
                <p className="sli-empty">No published results match the authorised school roster.</p>
              )}
              {detail.value.truncated && <p className="sli-help">Showing the first 100 results.</p>}
            </>
          )}
        </div>
      </SimpleModal>
    </div>
  );
}
